import { Group, User } from "@/types/users";
import {
  NEW_CONVERSATION_ADDRESS_NOT_FOUND,
  NEW_CONVERSATION_CATALINA_SUGGESTION,
  NEW_CONVERSATION_EMAIL_SUGGESTION,
  NEW_CONVERSATION_FEDERATED_SUGGESTION,
  NEW_CONVERSATION_PLACEHOLDER,
  NEW_CONVERSATION_SUGGESTION_PLACEHOLDER,
} from "@/constants/ui";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { Input } from "../ui/input";
import { cn } from "@/lib/utils";
import { generateSuggestions } from "@/utils/utils";
import {
  normalizeExternalAddress,
  resolveExternalDmAddressType,
} from "@/utils/addresses";
import {
  CATALINA_ADDRESS_TYPE,
  EMAIL_ADDRESS_TYPE,
  FEDERATION_ADDRESS_TYPE,
} from "@/constants/message";

// Keyed by address type so the label can't drift from what resolveExternalDmAddressType
// decided — adding a gateway is one entry here, not another branch to keep in order.
const EXTERNAL_SUGGESTION_LABELS: Record<number, (address: string) => string> = {
  [CATALINA_ADDRESS_TYPE]: NEW_CONVERSATION_CATALINA_SUGGESTION,
  [EMAIL_ADDRESS_TYPE]: NEW_CONVERSATION_EMAIL_SUGGESTION,
  [FEDERATION_ADDRESS_TYPE]: NEW_CONVERSATION_FEDERATED_SUGGESTION,
};

type SearchBarProps = {
  userList: User[];
  groupList: Group[];
  onSelectConversation: (address: string) => void;
};

const SearchBar = ({
  userList,
  groupList,
  onSelectConversation,
}: SearchBarProps) => {
  const [userInput, setUserInput] = useState<string>("");
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [cachedSuggestions, setCachedSuggestions] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // An external address lives outside this router, so it can never appear in the locally
  // fetched user list. Offer it as its own suggestion row so the feature is discoverable and
  // selecting it goes through the same path as picking a local user.
  const trimmedInput = userInput.trim();
  const externalAddress = normalizeExternalAddress(trimmedInput);
  const isKnownLocalUser = userList.some(
    (u) => u.username.toLowerCase() === trimmedInput.toLowerCase()
  );
  const externalType = isKnownLocalUser
    ? null
    : resolveExternalDmAddressType(externalAddress);
  const externalSuggestion = externalType
    ? EXTERNAL_SUGGESTION_LABELS[externalType](externalAddress)
    : null;
  const displayedSuggestions = externalSuggestion
    ? [externalSuggestion, ...cachedSuggestions]
    : cachedSuggestions;

  /**
   * Generates all users and groups in alphabetical order.
   *
   * @returns {string[]} An array of all users and groups sorted alphabetically.
   */
  const generateAllSuggestions = (): string[] => {
    const userSuggestions = userList.map((user) => {
      if (!user.firstname || !user.lastname) {
        return `${user.username}`;
      } else {
        return `${user.username} (${user.firstname} ${user.lastname})`;
      }
    });

    // Groups are intentionally excluded — users cannot start new group conversations.
    // const groupSuggestions = groupList.map(
    //   (group) => `${group.name} (Group)`
    // );

    return [...userSuggestions].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  };

  /**
   * Handles changes to the input box for the search bar.
   * It updates the cached suggestions + updates state for conditional styling.
   *
   * @param {ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newInput = e.target.value;

    // Typing normally clears the selection, disabling the start button until a suggestion is
    // picked. An external address has no suggestion to pick, so select it once it's well-formed.
    const updateState = () => {
      const external = normalizeExternalAddress(newInput);
      setUserInput(newInput);
      setIsSelected(false);
      setSelectedIndex(-1);
      onSelectConversation(resolveExternalDmAddressType(external) ? external : "");
    };

    // Check if userList and groupList are populated
    if (!userList.length && !groupList.length) {
      setCachedSuggestions([NEW_CONVERSATION_SUGGESTION_PLACEHOLDER]);
      updateState();
      return;
    }

    // If input is empty, show all suggestions alphabetically
    if (!newInput.trim()) {
      setCachedSuggestions(generateAllSuggestions());
      updateState();
      return;
    }

    // If typing forward, filter from cached suggestions, otherwise, regenerate suggestions
    if (userInput && newInput.startsWith(userInput)) {
      const newFilteredSuggestions = cachedSuggestions.filter((suggestion) => {
        return suggestion.toLowerCase().includes(newInput.trim().toLowerCase());
      });
      setCachedSuggestions(newFilteredSuggestions);
    } else {
      setCachedSuggestions(generateSuggestions(newInput, userList));
    }

    updateState();
  };

  /**
   * Sets the target of the new conversation to the username/group name selected.
   *
   * @param {string} userOrGroup - The user or group string that was clicked.
   */
  const selectSuggestion = (userOrGroup: string): void => {
    // Use the address we already hold rather than parsing the label back apart: the split below
    // would truncate at a space, and a Catalina address (leading "#" is the only rule) can
    // contain one.
    if (externalSuggestion && userOrGroup === externalSuggestion) {
      onSelectConversation(externalAddress);
      setUserInput(userOrGroup);
      setIsSelected(true);
      return;
    }

    // Groups are formatted as "GroupName (Group)" — strip the suffix to get the name.
    // Users are formatted as "username" or "username (First Last)" — take only the username.
    const isGroup = userOrGroup.endsWith(" (Group)");
    const name = isGroup
      ? userOrGroup.slice(0, -" (Group)".length)
      : userOrGroup.split(" ")[0];
    onSelectConversation(name);
    setUserInput(userOrGroup);
    setIsSelected(true);
  };

  /**
   * Handles keyboard navigation and selection within the suggestion list.
   *
   * - ArrowDown/Tab: Move down the suggestions list
   * - ArrowUp: Move up the suggestions list or focus back to the input box
   * - Enter: Select the current suggestion
   *
   * @param {React.KeyboardEvent<HTMLDivElement>} e - The keyboard event triggered by a key press.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      (e.key === "ArrowDown" || e.key === "Tab") &&
      selectedIndex < displayedSuggestions.length - 1
    ) {
      // Move down in the suggestions list
      e.preventDefault();
      setSelectedIndex((prev) => prev + 1);
    } else if (e.key === "ArrowUp") {
      // Either move focus to input box, or move up a suggestion
      e.preventDefault();

      if (selectedIndex === 0) {
        inputRef.current?.focus();
      } else if (selectedIndex > 0) {
        setSelectedIndex((prev) => prev - 1);
      }
    } else if (e.key === "Enter" && selectedIndex !== -1) {
      // Select the current option and put focus back on the input box
      selectSuggestion(displayedSuggestions[selectedIndex]);
      inputRef.current?.focus();
    }
  };

  // Shift focus from input to first option in suggestion list
  const shiftFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown" || (e.key === "Tab" && !isSelected)) {
      e.preventDefault(); // prevent cursor from moving
      setSelectedIndex(0);
    }
  };

  // Scrolls to the top of the suggestion list and resets the selected index
  const scrollToTop = () => {
    setSelectedIndex(-1);
    suggestionRefs.current[0]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  /**
   * Handles input focus event.
   * Shows all suggestions alphabetically when input is focused and empty.
   */
  const handleFocus = () => {
    setIsFocused(true);
    scrollToTop();
    
    // If input is empty, show all suggestions
    if (!userInput.trim() && userList.length > 0) {
      setCachedSuggestions(generateAllSuggestions());
    }
  };

  /**
   * Handles input blur event.
   */
  const handleBlur = () => {
    // Delay to allow click events on suggestions to fire
    setTimeout(() => {
      setIsFocused(false);
    }, 200);
  };

  // Handles focus & scrolling as we move up/down the suggestion list
  useEffect(() => {
    if (selectedIndex !== -1 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex].focus();
      suggestionRefs.current[selectedIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedIndex]);

  // Update sugggestions after the user + group lists finish loading in
  useEffect(() => {
    if (userList.length > 0 && groupList.length >= 0) {
      if (userInput.trim()) {
        setCachedSuggestions(generateSuggestions(userInput, userList));
      } else if (isFocused) {
        setCachedSuggestions(generateAllSuggestions());
      }
    }
  }, [userList, groupList, userInput, isFocused]);

  return (
    <div className="flex flex-col">
      <Input
        ref={inputRef}
        value={userInput}
        placeholder={NEW_CONVERSATION_PLACEHOLDER}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleInputChange(e)
        }
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={shiftFocus}
        className={cn(
          "rounded-md",
          {
            "rounded-b-none focus-visible:ring-transparent focus-visible:border-2 focus-visible:border-black":
              isFocused && !isSelected, // black border while focused and not selected
          },
          {
            "rounded-md focus-visible:ring-2 focus-visible:ring-black focus-visible:border-1 focus-visible:border-gray":
              isSelected, // reset border back to normal and use ring for focus
          }
        )}
      />

      {/* Suggestions container with fixed height */}
      <div className="h-28">
        {isFocused && !isSelected && (
          <div
            tabIndex={-1}
            // Clicking the native scrollbar is a mousedown on this container, and its
            // default action shifts DOM focus to the nearest focusable ancestor — this
            // div (tabIndex=-1) — which blurs the input and, after handleBlur's 200ms
            // timer, hides the whole dropdown before the user can actually scroll/select.
            // Suppressing the mousedown's default action keeps focus on the input
            // (native scrollbar dragging is unaffected) so the dropdown stays open.
            onMouseDown={(e) => e.preventDefault()}
            className="overflow-y-auto border border-t-0 rounded-b shadow-md max-h-28 focus:outline-none"
          >
            {displayedSuggestions.length > 0 ? (
              displayedSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  tabIndex={0}
                  ref={(el) => {
                    suggestionRefs.current[index] = el;
                  }}
                  onClick={() => {
                    selectSuggestion(suggestion);
                    inputRef.current?.focus();
                  }}
                  onKeyDown={handleKeyDown}
                  className={cn(
                    "px-3 py-2 cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
                    { "bg-gray-100": selectedIndex === index }
                  )}
                >
                  <p className="text-sm text-neutral-500">{suggestion}</p>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 rounded-b">
                <p className="text-sm text-neutral-500">
                  {NEW_CONVERSATION_ADDRESS_NOT_FOUND}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
